import { Controller } from "@hotwired/stimulus"
import ScriptedVideoChannel from "../../channels/scripted_video_channel"

// Per-scene generation controller — handles inline video playback,
// toggling the generation history panel, and scene video generation.
export default class extends Controller {
  static targets = [
    "videoContainer", "thumbnail", "videoPlayer", "playButton",
    "generateButton", "generateIcon", "generateSpinner",
    "emptyState", "generatingState", "detailModal"
  ]
  static values = { configId: String, sceneId: String }

  connect() {
    if (this.configIdValue) this.subscribeToChannel()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  async generateScene(event) {
    const button = event.currentTarget
    const url = button.dataset.url
    if (!url || button.disabled) return

    this.showGenerating()

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const body = new FormData()
      body.append("scene_id", this.sceneIdValue)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(data.error || `Request failed (${response.status})`)
      }
      // Job enqueued — spinner stays, ActionCable will deliver result
    } catch (error) {
      this.hideGenerating()
      console.error("[scene-generation] Generate failed:", error.message)
    }
  }

  playInline(event) {
    if (event) event.stopPropagation()
    if (!this.hasVideoPlayerTarget) return

    if (this.hasThumbnailTarget) this.thumbnailTarget.classList.add("hidden")
    if (this.hasPlayButtonTarget) this.playButtonTarget.classList.add("hidden")

    this.videoPlayerTarget.classList.remove("hidden")
    this.videoPlayerTarget.play()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  playVideo(event) {
    const videoPath = event.currentTarget.dataset.videoPath
    if (!videoPath) return

    window.open(videoPath, "_blank")
  }

  openDetailModal() {
    if (!this.hasDetailModalTarget) return

    const modalEl = this.detailModalTarget.querySelector("[data-controller='modal']")
    if (!modalEl) return

    const modalController = this.application.getControllerForElementAndIdentifier(modalEl, "modal")
    if (modalController) modalController.open()
  }

  // --- Private ---

  subscribeToChannel() {
    this.subscription = ScriptedVideoChannel.subscribe(this.configIdValue, {
      onSceneGenerationUpdate: (data) => this.handleSceneUpdate(data),
      onSceneExtensionUpdate: (data) => this.handleExtensionUpdate(data)
    })
  }

  handleSceneUpdate(data) {
    // Only handle updates for this scene
    if (data.scene_id && data.scene_id !== this.sceneIdValue) return

    if (data.status === "completed" || data.status === "failed" || data.status === "content_filtered") {
      // Reload the page so server-rendered state is correct
      // (shows failed banner, re-enables generate button, etc.)
      this.turboReload()
    }
  }

  handleExtensionUpdate(data) {
    if (data.scene_id && data.scene_id !== this.sceneIdValue) return

    if (data.status === "extended" || data.status === "extension_failed") {
      this.turboReload()
    }
  }

  showGenerating() {
    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.disabled = true
      this.generateButtonTarget.classList.add("cursor-not-allowed")
      this.generateButtonTarget.classList.remove("hover:text-green-600")
      this.generateButtonTarget.classList.add("text-blue-500")
      this.generateButtonTarget.classList.remove("text-gray-400")
    }
    if (this.hasGenerateIconTarget) this.generateIconTarget.classList.add("hidden")
    if (this.hasGenerateSpinnerTarget) this.generateSpinnerTarget.classList.remove("hidden")
    // Swap empty state → generating state in video area
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    if (this.hasGeneratingStateTarget) this.generatingStateTarget.classList.remove("hidden")
  }

  hideGenerating() {
    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.disabled = false
      this.generateButtonTarget.classList.remove("cursor-not-allowed")
      this.generateButtonTarget.classList.add("hover:text-green-600")
      this.generateButtonTarget.classList.remove("text-blue-500")
      this.generateButtonTarget.classList.add("text-gray-400")
    }
    if (this.hasGenerateIconTarget) this.generateIconTarget.classList.remove("hidden")
    if (this.hasGenerateSpinnerTarget) this.generateSpinnerTarget.classList.add("hidden")
  }

  turboReload() {
    if (typeof Turbo !== "undefined" && Turbo.visit) {
      Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }
}
